<template>
  <article class="card documents-table-card">
    <div class="card-body">
      <header v-if="showHeading" class="activity-history-table__header documents-table-card__title-header">
        <h4>{{ title }}</h4>
        <p v-if="subtitle !== ''">{{ subtitle }}</p>
      </header>

      <header class="documents-table-card__header">
        <div class="documents-table-card__header-actions">
          <button
            v-if="showDownloadAll"
            type="button"
            class="btn btn-light documents-table-card__download-all-btn"
            @click="$emit('download-all')"
          >
            <i class="fa fa-download" aria-hidden="true"></i>
            {{ text.tableCard.buttons.downloadAll }}
          </button>

          <button type="button" class="btn btn-primary documents-table-card__upload-btn" @click="$emit('open-create')">
            <i class="fa fa-plus" aria-hidden="true"></i>
            <span>{{ text.tableCard.buttons.create }}</span>
          </button>
        </div>
      </header>

      <section class="app-table-wrap">
        <div class="app-table-scroll">
          <table class="app-data-table">
            <thead>
              <tr>
                <th class="app-col-index">{{ text.tableCard.table.headers.index }}</th>
                <th>{{ text.tableCard.table.headers.name }}</th>
                <th v-if="showValidityColumn">{{ text.tableCard.table.headers.validity }}</th>
                <th v-if="showCommentsColumn">{{ text.tableCard.table.headers.comments }}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loading">
                <td :colspan="visibleColumnCount" class="app-table-empty">{{ text.tableCard.table.loading }}</td>
              </tr>

              <tr v-else-if="rows.length === 0">
                <td :colspan="visibleColumnCount" class="app-table-empty">{{ text.tableCard.table.empty }}</td>
              </tr>

              <tr v-else v-for="(row, index) in rows" :key="row.id || `row-${index}`">
                <td class="app-muted">{{ index + 1 }}</td>
                <td class="app-strong">{{ row.name }}</td>
                <td v-if="showValidityColumn" class="documents-table-card__validity-cell">
                  <template v-if="hasAnyValidity(row)">
                    <span v-if="normalizeDateValue(row.validityStart) !== ''"><strong>{{ text.tableCard.table.validityStart }}</strong> {{ normalizeDateValue(row.validityStart) }}</span>
                    <span v-if="normalizeDateValue(row.validityEnd) !== ''"><strong>{{ text.tableCard.table.validityEnd }}</strong> {{ normalizeDateValue(row.validityEnd) }}</span>
                  </template>
                  <span v-else>{{ text.common.noValidity }}</span>
                </td>
                <td v-if="showCommentsColumn">{{ row.comments }}</td>
                <td class="documents-table-card__actions-cell">
                  <div class="documents-table-card__actions">
                    <button
                      type="button"
                      class="app-table-icon-btn app-table-icon-btn--view"
                      :title="text.tableCard.table.actionTitles.download"
                      @click="$emit('download-item', row)"
                    >
                      <i class="fa fa-download" aria-hidden="true"></i>
                    </button>

                    <button
                      v-if="showEditAction"
                      type="button"
                      class="app-table-icon-btn app-table-icon-btn--edit"
                      :title="text.tableCard.table.actionTitles.edit"
                      @click="$emit('edit-item', row)"
                    >
                      <i class="fa fa-pencil" aria-hidden="true"></i>
                    </button>

                    <button
                      v-if="showDeleteAction"
                      type="button"
                      class="app-table-icon-btn app-table-icon-btn--delete"
                      :title="text.tableCard.table.actionTitles.delete"
                      @click="$emit('delete-item', row)"
                    >
                      <i class="fa fa-trash" aria-hidden="true"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>
  </article>
</template>

<script>
import { useDomainText } from '@/composables/text/useDomainText';

const { catalog: text } = useDomainText('documents');

export default {
  name: 'DocumentsTableCard',
  emits: ['download-all', 'open-create', 'download-item', 'edit-item', 'delete-item'],
  props: {
    rows: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    showDownloadAll: {
      type: Boolean,
      default: true,
    },
    showValidityColumn: {
      type: Boolean,
      default: true,
    },
    showCommentsColumn: {
      type: Boolean,
      default: true,
    },
    showEditAction: {
      type: Boolean,
      default: true,
    },
    showDeleteAction: {
      type: Boolean,
      default: true,
    },
    title: {
      type: String,
      default: '',
    },
    subtitle: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      text,
    };
  },
  computed: {
    showHeading() {
      return this.title !== '' || this.subtitle !== '';
    },
    visibleColumnCount() {
      return 3 + (this.showValidityColumn ? 1 : 0) + (this.showCommentsColumn ? 1 : 0);
    },
  },
  methods: {
    normalizeDateValue(value) {
      const normalized = String(value || '').trim();
      return normalized === this.text.common.emptyValue ? '' : normalized;
    },
    hasAnyValidity(row) {
      return this.normalizeDateValue(row?.validityStart) !== '' || this.normalizeDateValue(row?.validityEnd) !== '';
    },
  },
};
</script>

<style scoped>
.documents-table-card__title-header {
  margin-bottom: 8px;
}

.documents-table-card__header {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-bottom: 12px;
}

.documents-table-card__header-actions {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.documents-table-card__download-all-btn {
  min-height: 34px;
  padding: 0 12px;
  font-size: 13px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.documents-table-card__upload-btn {
  min-height: 34px;
  padding: 0 16px;
  font-size: 13px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.documents-table-card__actions-cell {
  width: 1%;
  white-space: nowrap;
}

.documents-table-card__validity-cell {
  min-width: 170px;
}

.documents-table-card__validity-cell span {
  display: block;
  line-height: 1.3;
}

.documents-table-card__actions {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
</style>
